/**
 * WebSocket Manager Hook
 * Manages WebSocket connection with debug message handling and host communication
 */

import { useState, useRef, useEffect } from 'react';
import { useWebSocket } from './useWebSocket';
import { FunctionCallRequest } from 'services/websocketService';

interface UseWebSocketManagerProps {
  apiHost: string;
  token: string | undefined;
  isDebugMode: boolean;
  isWsDebugEnabled: boolean;
  validationMode: boolean;
  contentRef: React.RefObject<HTMLDivElement>;
}

export function useWebSocketManager({
  apiHost,
  token,
  isDebugMode,
  isWsDebugEnabled,
  validationMode,
  contentRef
}: UseWebSocketManagerProps) {
  // Debug messages state (separate from chat history, not sent to server)
  const [debugMessages, setDebugMessages] = useState<Array<{
    message: string;
    timestamp: number;
    direction: 'sent' | 'received';
  }>>([]);

  // Use ref to track current validation mode for WebSocket callback
  const validationModeRef = useRef(validationMode);
  useEffect(() => {
    validationModeRef.current = validationMode;
  }, [validationMode]);

  // Handle debug messages
  const handleDebugMessage = (direction: 'sent' | 'received', message: any) => {
    if (isDebugMode && isWsDebugEnabled) {
      const timestamp = new Date(message.timestamp || Date.now()).toLocaleTimeString();
      const debugContent = `[${timestamp}] WebSocket ${direction === 'sent' ? '→' : '←'} ${message.type}\n${JSON.stringify(message.payload, null, 2)}`;
      
      // In validation mode, append to contentRef
      if (validationModeRef.current && contentRef.current) {
        const debugElement = document.createElement('div');
        debugElement.className = `debug-message ${direction}`;
        debugElement.textContent = debugContent;
        
        contentRef.current.appendChild(debugElement);
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
      // In chat mode, add to debug messages (not chat history)
      else if (!validationModeRef.current) {
        setDebugMessages(prev => [...prev, {
          message: debugContent,
          timestamp: Date.now(),
          direction: direction
        }]);
      }
    }
  };

  // Handle function call requests from server
  const handleFunctionCallRequest = async (request: FunctionCallRequest) => {
    // Forward function call to host application
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const message = {
        "Type": "FunctionCall",
        "Data": JSON.stringify(request)
      };
      
      // Send message to host (e.g., C# WebView in Revit plugin)
      // eslint-disable-next-line
      eval('window.chrome?.webview?.postMessage(message);');
    } catch (error) {
      console.error('Failed to forward function call to host:', error);
    }
  };

  // WebSocket connection
  const { isConnected: isWebSocketConnected } = useWebSocket({
    apiHost,
    token,
    autoConnect: true,
    debugMode: isDebugMode && isWsDebugEnabled,
    onDebugMessage: handleDebugMessage,
    onFunctionCallRequest: handleFunctionCallRequest
  });

  return {
    isWebSocketConnected,
    debugMessages,
    clearDebugMessages: () => setDebugMessages([])
  };
}
